import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import RootRef from '@material-ui/core/RootRef';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import HCPCSCodesTableEmpty from './HCPCSCodesTableEmpty';
import HCPCSCodesTableRow from './HCPCSCodesTableRow';

const styles = theme => ({
  paper: {
    position: 'relative',
    width: '100%',
    height: 'calc(100vh - 250px)',
    overflowX: 'auto',
    overflowY: 'visible',
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 178px)',
      transition: theme.transitions.create(['height'], {
        duration: theme.transitions.duration.short,
      }),
    },
    '& td': {
      paddingLeft: 8,
      paddingRight: 8,
    },
    '& th': {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  paperMobileFilterExpanded: {
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 268px)',
    },
  },
  table: {
    minWidth: 500,
  },
  hiddenCell: {
    fontSize: 0,
  },
  tableHead: {
    position: 'sticky',
    zIndex: 20,
    top: 0,
    '& $rowGroup th:first-child': {
      borderTopLeftRadius: 10,
    },
    '& $rowGroup th:last-child': {
      borderTopRightRadius: 10,
    },
  },
  rowGroup: {
    height: 40,
  },
  cellGroup: {
    paddingBottom: 8,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  cellMedicaidGroup: {
    color: theme.palette.primary[500],
  },
  cellMedicareGroup: {
    color: theme.palette.notification[700],
  },
  cellMedicaid: {
    backgroundColor: theme.palette.primary[100],
  },
  cellMedicare: {
    backgroundColor: theme.palette.notification[100],
  },
  cellMedicaidFirst: {
    position: 'relative',
    borderTopLeftRadius: 10,
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: -1,
      width: 10,
      height: 10,
      backgroundColor: theme.palette.primary['A200'],
    },
  },
  cellMedicaidLast: {
    position: 'relative',
    borderTopRightRadius: 10,
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: -1,
      width: 10,
      height: 10,
      backgroundColor: theme.palette.primary['A200'],
    },
  },
  cellMedicareFirst: {
    position: 'relative',
    borderTopLeftRadius: 10,
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: -1,
      width: 10,
      height: 10,
      backgroundColor: theme.palette.primary['A200'],
    },
  },
  cellMedicareLast: {
    position: 'relative',
    borderTopRightRadius: 10,
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: -1,
      width: 10,
      height: 10,
      backgroundColor: theme.palette.primary['A200'],
    },
  },
  typography: {
    paddingLeft: 10,
    paddingBottom: 8,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
});

const HCPCSCodesTable = ({
  items,
  count,
  classes,
  loading,
  State,
  mobileFilterExpanded,
  onScroll,
  scrollRef,
}) => (
  <div>
    {(loading === 0 || items.length > 0) && (
      <Typography className={classes.typography}>
        {`(${count})`}
      </Typography>
    )}
    <RootRef rootRef={scrollRef}>
    <Paper
      className={classNames(classes.paper, {
        [classes.paperMobileFilterExpanded]: mobileFilterExpanded,
      })}
      onScroll={onScroll}
    >
    {(loading === 0 && items.length === 0) &&
      <HCPCSCodesTableEmpty />
    }
    {items.length > 0 &&
      <Table className={classes.table}>
        <TableHead
          className={classes.tableHead}
        >
          <TableRow className={classes.rowGroup}>
            <TableCell className={classes.cellGroup} colSpan="2" />
            <TableCell className={classNames(classes.cellGroup, classes.cellMedicaidGroup)} colSpan="6">Medicaid</TableCell>
            <TableCell className={classNames(classes.cellGroup, classes.cellMedicareGroup)} colSpan="5">Medicare</TableCell>
            <TableCell className={classNames(classes.cellGroup, classes.hiddenCell)}>Actions</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Modifiers</TableCell>
            <TableCell className={classNames(classes.cellMedicaid, classes.cellMedicaidFirst)}>Description</TableCell>
            <TableCell className={classes.cellMedicaid}>Urban <br />Purchase</TableCell>
            <TableCell className={classes.cellMedicaid}>Rural <br />Purchase</TableCell>
            <TableCell className={classes.cellMedicaid}>Urban <br />Rental</TableCell>
            <TableCell className={classNames(classes.cellMedicaid, classes.cellMedicaidLast)}>Rural <br />Rental</TableCell>
            <TableCell />
            <TableCell className={classNames(classes.cellMedicare, classes.cellMedicareFirst)}>Description</TableCell>
            <TableCell className={classes.cellMedicare}>Urban <br />Purchase</TableCell>
            <TableCell className={classes.cellMedicare}>Rural <br />Purchase</TableCell>
            <TableCell className={classes.cellMedicare}>Urban <br />Rental</TableCell>
            <TableCell className={classNames(classes.cellMedicare, classes.cellMedicareLast)}>Rural <br />Rental</TableCell>
            <TableCell className={classes.hiddenCell}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <HCPCSCodesTableRow
              key={item.ID}
              {...item}
              State={State}
            />
          ))}
        </TableBody>
      </Table>
    }
    </Paper>
    </RootRef>
  </div>
);

HCPCSCodesTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number,
  State: PropTypes.string.isRequired,
  mobileFilterExpanded: PropTypes.bool,
  onScroll: PropTypes.func,
  scrollRef: PropTypes.func,
  classes: PropTypes.shape({
    paper: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
    hiddenCell: PropTypes.string.isRequired,
  }).isRequired,
};

HCPCSCodesTable.defaultProps = {
  loading: 0,
  mobileFilterExpanded: false,
  onScroll: undefined,
  scrollRef: undefined,
};

export default withStyles(styles)(HCPCSCodesTable);



// WEBPACK FOOTER //
// ./src/components/settings/HCPCSCodesTable/HCPCSCodesTable.js